import {policy} from './policy.js';
import {t,htmlMessage,onLanguageChange,localeState} from './i18n.js';
import styleText from './engine.css?raw';
import { safeHtml, htmlText, keepParagraphEndings } from './document.js';
import {wordAtPoint} from './audio-position.js';
import {downloadWidget,openPdfPreview} from './book-downloads.js';
import {bookCover,prepareCover} from './covers.js';
import {pdfView} from './pdf.js';
import {prepareReadingFont} from './reading-font-loader.js';
import {chapterText,textContext} from './text-anchors.js';

function openingControls(cover=null){
  const opening=document.createElement('div');opening.className='reader-opening';
  if(cover)opening.append(cover);
  const controls=document.createElement('slot');controls.name='opening-controls';opening.append(controls);
  return opening;
}

export class ReaderEngine extends HTMLElement {
  constructor(){super();this.attachShadow({mode:'open'});this.shadowRoot.innerHTML=`<style>${styleText}</style><div id="stage"></div>`;this.stage=this.shadowRoot.querySelector('#stage');this.settings={};this.book=null;this.emit=()=>{};this.pageIndex=0;this.visible=new Set();this.lastScroll=0;this.zoom=1;this.generation=0;this.restoring=false;this.boundScroll=()=>this.scheduleProgress();this.resizeTimer=0;this.currentPace=220;this.resetReadingPace();}
  connectedCallback(){this.localeSubscription=onLanguageChange(()=>this.localize());window.addEventListener('scroll',this.boundScroll,{passive:true});this.resizeObserver=new ResizeObserver(()=>this.scheduleResize());this.resizeObserver.observe(this);this.resizeObserver.observe(this.stage);this.shadowRoot.addEventListener('click',event=>this.linkClick(event));this.shadowRoot.addEventListener('mouseup',()=>this.selection());this.shadowRoot.addEventListener('touchend',()=>setTimeout(()=>this.selection(),100));this.listenForGestures();}
  disconnectedCallback(){this.localeSubscription?.();window.removeEventListener('scroll',this.boundScroll);this.gestureAbort?.abort();this.resizeObserver?.disconnect();this.cleanup();}
  localize(){
    const root=this.shadowRoot;
    for(const element of root.querySelectorAll('.chapter-kicker')){const index=Number(element.parentElement.dataset.index);element.textContent=this.sectionLabel(this.book.chapters[index],index);element.lang=localeState().language;element.dir=localeState().direction;}
    for(const element of root.querySelectorAll('.blank-explanation')){element.textContent=t('ui-this-chapter-is-intentionally-blank');element.lang=localeState().language;}
    for(const [action,key]of Object.entries({previous:'ui-previous-page',next:'ui-next-page',menu:'ui-open-reader-menu',appearance:'ui-open-appearance-menu'}))root.querySelector('.tap-edge.'+action)?.setAttribute('aria-label',t(key));
    for(const [zoom,key]of Object.entries({'-':'ui-zoom',fit:'ui-fit-width','+':'ui-zoom-2'})){const button=root.querySelector('[data-zoom="'+zoom+'"]');if(button)button.textContent=t(key);}
    this.scheduleProgress();
  }

  gestureBlocked(event){return !this.book||this.restoring||this.pdf||this.pickingAudio||document.querySelector('.panel-overlay,:popover-open')||this.annotations?.isOpen()||this.hasTextSelection()||event.composedPath().some(el=>el?.matches?.('a,button,input,textarea,select,iframe,video,audio,[contenteditable],[data-highlight-id],.selection-tools,book-downloads'));}
  hasTextSelection(){return !!(this.shadowRoot.getSelection?.()||window.getSelection())?.toString().trim();}
  horizontalScroller(event){
    // A nested viewport owns its horizontal gestures, including at its limits.
    // The paginated flow itself also overflows horizontally, but is not one.
    for(const el of event.composedPath()){
      if(el===this.flow||el===this.stage||el===this)break;
      if(el instanceof HTMLElement&&el.clientWidth>0&&el.scrollWidth>el.clientWidth+1&&['auto','scroll'].includes(getComputedStyle(el).overflowX))return el;
    }
    return null;
  }
  listenForGestures(){
    this.gestureAbort?.abort();this.gestureAbort=new AbortController();
    const options={signal:this.gestureAbort.signal};
    const resetPace=()=>this.resetReadingPace();
    document.addEventListener('visibilitychange',resetPace,options);
    document.addEventListener('toggle',resetPace,{...options,capture:true});
    window.addEventListener('blur',resetPace,options);
    window.addEventListener('focus',resetPace,options);
    this.addEventListener('play',resetPace,{...options,capture:true});
    this.addEventListener('pause',resetPace,{...options,capture:true});
    this.addEventListener('touchstart',event=>{
      this.gesture=null;this.scrollHandoff=null;this.suppressTapUntil=0;
      if(event.touches.length!==1||this.gestureBlocked(event))return;
      this.stopPageTurn();
      const touch=event.touches[0];this.gesture={id:touch.identifier,x:touch.clientX,y:touch.clientY,dx:0,dy:0,time:performance.now(),axis:null,tap:this.settings.mode==='tap',scrollY:window.scrollY,scroller:this.horizontalScroller(event)};
    },{...options,passive:true});
    this.addEventListener('touchmove',event=>{
      const gesture=this.gesture;if(!gesture)return;
      if(event.touches.length!==1||this.hasTextSelection()){this.gesture=null;return;}
      const touch=[...event.touches].find(touch=>touch.identifier===gesture.id);if(!touch)return;
      gesture.dx=touch.clientX-gesture.x;gesture.dy=touch.clientY-gesture.y;
      const x=Math.abs(gesture.dx),y=Math.abs(gesture.dy);
      if(!gesture.axis&&Math.max(x,y)>12){
        if(x>y*1.4){if(performance.now()-gesture.time>500){this.gesture=null;this.suppressTapUntil=performance.now()+700;return;}gesture.axis='x';}
        else if(y>x*1.4)gesture.axis='y';
      }
      // Keep native momentum and browser-bar movement. Once the drag is deliberate,
      // transfer the distance already travelled under the fixed page into Scroll.
      if(gesture.axis==='x'&&!gesture.scroller)event.preventDefault();
      else if(gesture.axis==='y'&&gesture.tap&&y>=24){
        gesture.tap=false;this.scrollHandoff={gesture,scrollY:gesture.scrollY,time:performance.now()};
        this.emit('readingGesture',{action:'scroll',visible:gesture.dy>0});
      }
      if(Math.max(x,y)>12)this.suppressTapUntil=performance.now()+700;
    },{...options,passive:false});
    this.addEventListener('touchend',event=>{
      const gesture=this.gesture;if(!gesture)return;
      if(![...event.changedTouches].some(touch=>touch.identifier===gesture.id))return;
      this.gesture=null;
      // A held press belongs to native text selection, even before the browser
      // exposes the selected range. Its synthetic click must not turn a page.
      if(!gesture.axis&&performance.now()-gesture.time>=500){this.suppressTapUntil=performance.now()+700;return;}
      if(this.hasTextSelection())return;
      if(gesture.axis==='x'&&!gesture.scroller&&Math.abs(gesture.dx)>=48)this.emit('readingGesture',{action:(gesture.dx<0)!==(this.flow.dir==='rtl')?'next':'previous',source:'swipe'});
    },options);
    this.addEventListener('touchcancel',()=>{this.gesture=null;this.suppressTapUntil=performance.now()+700;},options);
    this.addEventListener('wheel',event=>{
      if(this.settings.mode!=='tap'||event.ctrlKey||this.gestureBlocked(event)||Math.abs(event.deltaY)<=Math.abs(event.deltaX))return;
      this.stopPageTurn();
      const now=performance.now(),delta=event.deltaY*(event.deltaMode===1?this.settings.font*this.settings.line:event.deltaMode===2?innerHeight:1);
      if(!this.scrollHandoff||this.scrollHandoff.gesture||now-this.scrollHandoff.time>180)this.scrollHandoff={delta:0,scrollY:window.scrollY,time:now};
      const handoff=this.scrollHandoff;handoff.delta+=delta;handoff.time=now;
      if(!handoff.requested&&Math.abs(handoff.delta)>=24){handoff.requested=true;this.emit('readingGesture',{action:'scroll',visible:handoff.delta<0});}
    },{...options,passive:true});
    // Stop synthetic clicks from drags and held presses before links/marks.
    this.addEventListener('click',event=>{if(event.detail>0&&performance.now()<this.suppressTapUntil){event.preventDefault();event.stopImmediatePropagation();}},{...options,capture:true});
    this.addEventListener('click',event=>{
      if(event.defaultPrevented||event.detail===0||this.gestureBlocked(event)||this.horizontalScroller(event))return;
      const viewport=window.visualViewport;
      const x=(event.clientX-(viewport?.offsetLeft||0))/(viewport?.width||innerWidth),y=(event.clientY-(viewport?.offsetTop||0))/(viewport?.height||innerHeight);
      const center=x>=.25&&x<=.75;
      if(this.settings.mode!=='tap'&&center&&y>=.3&&y<=.7){event.preventDefault();this.emit('readingGesture',{action:'chrome'});return;}
      if(this.settings.mode!=='tap')return;
      event.preventDefault();
      if(y<.125||(center&&y<.2))this.emit('readingGesture',{action:'menu'});
      else if(y>.875||(center&&y>.8))this.emit('readingGesture',{action:'appearance'});
      else this.emit('readingGesture',{action:(this.flow.dir==='rtl'?x>.75:x<.25)?'previous':'next'});
    },options);
  }
  async cleanup(){this.searchBlocks=null;this.stopFlowPreparation();this.stopPageTurn();this.clearBoundaryCue();this.gesture=null;this.scrollHandoff=null;this.pageTurning=false;this.pendingResizePosition=null;this.resizeAnchor=null;this.style.height='';this.annotations?.destroy();this.annotations=null;this.generation++;this.observer?.disconnect();if(this.pdf?.url)URL.revokeObjectURL(this.pdf.url);this.pdf=null;clearTimeout(this.progressTimer);this.progressTimer=0;clearTimeout(this.resizeTimer);}
  async open(book,file,position,settings){this.resetReadingPace();await this.cleanup();this.book=book;this.settings=settings;this.position=position;this.restoring=true;this.tapOrigin=null;this.stage.classList.remove('tap-shell');this.stage.innerHTML='';this.zoom=1;const generation=this.generation;
    if(book.kind==='pdf')await this.openPdf(file,position);
    else{
      position=await this.openFlow(position);
      if(generation!==this.generation)return;
      this.position=position;
      if(settings.mode==='tap'||position&&!Number.isFinite(position.openingScrollY)){
        await new Promise(resolve=>requestAnimationFrame(resolve));
        if(generation!==this.generation)return;
        if(settings.mode==='tap')this.layoutFlow(position);else this.restoreFlow(position);
      }
    }
    if(generation!==this.generation)return;
    this.restoring=false;this.scheduleProgress();
  }
  async openFlow(position){
    const generation=this.generation;
    this.stage.innerHTML='<div class="book-flow"></div>';this.flow=this.stage.firstElementChild;this.flow.lang=this.book.language || 'en';this.flow.dir=this.book.direction || 'ltr';
    this.chapterLoads=new Map();this.navigationEpoch=(this.navigationEpoch||0)+1;
    const chapters=this.book.chapters.map(chapter=>{const holder=document.createElement('template');holder.innerHTML=chapter.html||'';return {...chapter,headings:[...holder.content.querySelectorAll('h2[id],h3[id],h4[id],h5[id],h6[id],p.section-heading[id]')].map(heading=>({id:heading.id,title:heading.textContent}))};});
    this.chapterPlan=await policy('readerPlan',{chapters,count:this.book.chapterCount??chapters.length,height:innerHeight,width:innerWidth,settings:this.settings});if(generation!==this.generation)return;this.chapterIds=new Map(this.chapterPlan.byId);
    for(const [index,c]of this.book.chapters.entries()){
      const section=document.createElement('section');section.className='book-chapter chapter-pending';section.id=c.id;section.dataset.index=index;
      section.style.setProperty('--chapter-estimate',this.chapterPlan.chapters[index].estimate+'px');
      this.flow.append(section);
    }
    const required=this.chapterIndex(position);
    await this.prepareFlowChapter(required,false);
    if(generation!==this.generation)return;
    position=await this.resolveFlowPosition(position);
    if(generation!==this.generation)return;
    if(this.settings.mode==='tap')this.tapChapter=required;
    this.emitToc();
    this.observeBlocks();this.layoutFlow(position);this.scheduleFlowPreparation();
    return position;
  }
  chapterIndex(position){
    const named=this.chapterIds?.get(position?.chapterId);
    if(Number.isInteger(named))return named;
    if(Number.isInteger(position?.chapter))return Math.max(0,Math.min(this.book.chapters.length-1,position.chapter));
    const block=this.chapterIds?.get(position?.block);if(Number.isInteger(block))return block;
    return 0;
  }
  readingChapters(){
    if(!this.flow||this.pdf)return [];
    if(this.settings.mode==='tap')return [this.tapChapter||0];
    return [...this.flow.children].filter(el=>{const r=el.getBoundingClientRect();return r.bottom>0&&r.top<innerHeight+200;}).map(el=>Number(el.dataset.index));
  }
  suspendOffscreenChapters(){
    if(!this.flow||this.pdf)return;
    const visible=new Set(this.readingChapters());
    for(const section of this.flow.children){
      if(visible.has(Number(section.dataset.index))||section.classList.contains('chapter-pending'))continue;
      const height=section.getBoundingClientRect().height;
      if(height)section.style.setProperty('--chapter-estimate',height+'px');
      section.classList.add('chapter-pending');section.style.contentVisibility='';
    }
  }
  async prepareFlowChapter(index,preserve=true){
    const section=this.flow?.children[index];if(!section)return;
    const generation=this.generation,family=this.settings.family;
    const key=index+':'+family;
    if(this.chapterLoads.has(key))return this.chapterLoads.get(key);
    const pending=(async()=>{
      await prepareReadingFont(family,this.book,[index]);
      if(generation!==this.generation)return;
      if(family!==this.settings.family)return this.prepareFlowChapter(index,preserve);
      // Preserve the viewport's chapter even when it is still a placeholder.
      // A previously saved text anchor may belong to a different chapter after
      // a scrollbar jump, and would pull the reader back to the wrong place.
      const anchor=preserve&&!this.restoring&&this.settings.mode!=='tap'?[...this.flow.children].find(el=>el.getBoundingClientRect().bottom>140):null;
      const top=anchor?.getBoundingClientRect().top;
      const flow=this.flow,anchorEpoch=anchor?this.flowAnchorEpoch=(this.flowAnchorEpoch||0)+1:this.flowAnchorEpoch;
      // Let this measured adjustment own the change, rather than competing
      // with the browser's automatic scroll anchoring in the same frame.
      if(anchor)flow.style.overflowAnchor='none';
      this.appendFlowChapter(index);
      section.classList.remove('chapter-pending');
      section.style.contentVisibility='visible';
      this.chapterObserver?.unobserve(section);
      if(anchor){
        window.scrollBy({top:anchor.getBoundingClientRect().top-top,behavior:'instant'});
        requestAnimationFrame(()=>requestAnimationFrame(()=>{if(this.flow===flow&&this.flowAnchorEpoch===anchorEpoch)flow.style.overflowAnchor='';}));
      }
      this.scheduleProgress();
    })().finally(()=>{if(generation===this.generation&&this.chapterLoads.get(key)===pending)this.chapterLoads.delete(key);});
    this.chapterLoads.set(key,pending);return pending;
  }
  emitToc(){this.emit('toc',this.chapterPlan.toc);}
  sectionLabel(chapter,index){const label=this.chapterPlan.chapters[index].label;return (label.message?t(label.message):'')+label.text;}
  appendFlowChapter(i){
    const existing=this.flow.children[i];if(!existing||existing.dataset.prepared)return;
    const c=this.book.chapters[i];
    const chapter=existing;chapter.dataset.prepared='true';
    const kicker=document.createElement('p');kicker.className='chapter-kicker';kicker.lang=localeState().language;kicker.dir=localeState().direction;kicker.textContent=this.sectionLabel(c,i);
    const title=document.createElement('h2');title.className='chapter-title';title.id=c.id+'-audio-title';title.textContent=c.title;chapter.append(kicker,title);
    const body=document.createElement('div');body.className='chapter-body';body.innerHTML=safeHtml(c.html,this.book.assetBase,image=>{image.loading='lazy';if(i===0)prepareCover(image,this.book);});chapter.append(body);keepParagraphEndings(body);
    if(this.book.id==='easypeasy-ar'&&c.id==='chapter-1'){
      // This source figure repeats the opening cover under a different filename.
      const duplicate=[...body.querySelectorAll('img')].find(image=>image.src===new URL('images/غلاف-الطبعة.jpeg',this.book.assetBase).href);
      const caption=body.querySelector('.edition-cover-caption');
      if(duplicate&&caption){duplicate.remove();const anchor=document.createElement('span');anchor.id=caption.id;caption.replaceWith(anchor);}
    }
    if(this.book.id==='easypeasy-en'&&i===0){
      const video=body.querySelector('iframe.book-video-audiobook');
      if(video){
        const shell=video.closest('.book-video-shell'),note=shell.nextElementSibling;
        // Keep the original links in source/no-JS editions; the player exposes them here.
        if(note?.matches('p')&&note.querySelector('a[href*="IXZT8dEfxSs"]')&&note.querySelector('a[href*="drive.google.com"]'))note.remove();
        const slot=document.createElement('slot');slot.name='inline-audiobook';shell.after(slot);
        this.videoPreview(video);
      }
    }
    if(this.book.id==='easypeasy-en'&&(i===0||c.id==='now')){
      // Replace the authored social footer with downloads, retaining its anchor.
      const footer=[...body.querySelectorAll('p')].find(p=>p.querySelector('a[href*="urbit.org"]')&&p.querySelector('a[href*="reddit.com"]'));
      const widget=downloadWidget(this.book);
      if(footer){widget.id=footer.id;footer.replaceWith(widget);}
      else if(i===0){const context=[...body.querySelectorAll('p')].find(p=>p.textContent.startsWith('A number of communities exist'));if(context)context.after(widget);}
    }
    if(i===0){
      let cover=null;
      if(this.book.id.startsWith('easypeasy-')){
        const image=body.querySelector('img[data-reader-cover]');
        if(image){
          image.loading='lazy';
          const wrapper=image.closest('figure') || image.closest('p');
          cover=wrapper && !wrapper.textContent.trim()?wrapper:image;
        }else if(this.book.cover){
          cover=document.createElement('img');cover.loading='lazy';cover.src=bookCover(this.book)?new URL(bookCover(this.book).source,document.baseURI).href:this.book.cover;cover.alt='';prepareCover(cover,this.book);
        }
      }
      chapter.classList.add('book-opening');chapter.prepend(openingControls(cover));
    }
    if(c.empty){const blank=document.createElement('div');blank.className='intentional-blank';blank.innerHTML=`<p class="blank-explanation" lang="${localeState().language}">${htmlMessage("ui-this-chapter-is-intentionally-blank")}</p>`;chapter.append(blank);}
    chapter.querySelectorAll('img').forEach(img=>img.addEventListener('load',()=>{if(this.settings.mode==='tap')this.resize(true);},{once:true}));
    for(const el of [chapter,...chapter.querySelectorAll('[id],.intentional-blank')])this.observer?.observe(el);
    this.annotations?.paint(chapter);
  }
  ensureFlowThrough(index=this.book?.chapters?.length-1){
    if(!this.flow||!this.book?.chapters||this.pdf)return;
    const last=Math.min(this.book.chapters.length-1,index);
    for(let i=0;i<=last;i++)this.appendFlowChapter(i);
  }
  ensureFlowPosition(position){
    this.appendFlowChapter(this.chapterIndex(position));
  }
  flowAnchor(position){
    const id=position?.block||position?.chapterId;if(!id)return null;
    const chapter=[...(this.flow?.children||[])].find(chapter=>chapter.id===position.chapterId);
    if(chapter){
      if(chapter.id===id)return chapter;
      const local=chapter.querySelector('#'+CSS.escape(id));if(local)return local;
    }
    return this.shadowRoot.getElementById(id);
  }
  async resolveFlowPosition(position){
    if(!position||Number.isFinite(position.openingScrollY))return position;
    const saved=this.annotations?.items.find(item=>position.highlight?item.id===position.highlight:item.location===JSON.stringify(position));
    const first=saved?.segments?.[0];
    const segment=first&&typeof first.quote==='string'&&typeof first.block==='string'&&Number.isInteger(first.start)&&first.start>=0&&first.chapterId===position.chapterId?first:null;
    const candidate=segment?{quote:segment.quote.slice(0,96),prefix:segment.context?.prefix||'',suffix:segment.quote.length>96?segment.quote.slice(96,160):segment.context?.suffix||'',offset:0}:position.anchor||
      (typeof saved?.excerpt==='string'?{quote:saved.excerpt.slice(0,96),prefix:'',suffix:saved.excerpt.slice(96,160),offset:0}:null);
    const anchor=candidate&&typeof candidate.quote==='string'&&candidate.quote.length<=96&&typeof candidate.prefix==='string'&&candidate.prefix.length<=64&&typeof candidate.suffix==='string'&&candidate.suffix.length<=64&&Number.isInteger(candidate.offset)&&candidate.offset>=0&&candidate.offset<=candidate.quote.length?candidate:null;
    // Chapter links and in-session navigation carry no saved prose to recover.
    if(!anchor&&!position.version&&!Number.isFinite(position.percent))return position;
    const blockId=segment?.block||position.block||position.chapterId;
    const oldOffset=segment?.start??position.textOffset??0;
    const version=segment?.context?.version||position.version;
    const original=this.flowAnchor({...position,block:blockId});
    if(version===this.book.version&&original&&(!anchor||original.textContent.slice(Math.max(0,oldOffset-(anchor.offset||0)),Math.max(0,oldOffset-(anchor.offset||0))+anchor.quote.length)===anchor.quote))return segment?{...position,block:segment.block,textOffset:segment.start,textY:140}:position;
    const index=this.chapterIndex(position),chapter=this.flow.children[index],facts=chapterText(chapter);
    const before=this.book.chapters.slice(0,index).reduce((sum,item)=>sum+item.wordCount,0);
    const total=this.book.wordCount||this.book.chapters.reduce((sum,item)=>sum+item.wordCount,0)||1;
    const fraction=Number.isFinite(position.chapterFraction)?position.chapterFraction:Number.isFinite(position.percent)?
      Math.max(0,Math.min(1,(position.percent/100*total-before)/Math.max(1,this.book.chapters[index].wordCount))):null;
    const [match]=await policy('resolveAnchors',{text:facts.text,blocks:facts.blocks,targets:[{block:blockId,start:Math.max(0,oldOffset-(anchor?.offset||0)),quote:anchor?.quote||'',prefix:anchor?.prefix||'',suffix:anchor?.suffix||'',approximate:true,fraction}]});
    if(!match)return position;
    const textOffset=match.start+(match.match==='nearby'?0:anchor?.offset||0);
    const element=facts.elements.get(match.block),quoteStart=Math.max(0,textOffset-32);
    const context=textContext(element,quoteStart,Math.min(element?.textContent.length||0,quoteStart+96));
    // A recovered character anchor must win over the retired element's height.
    return {...position,chapter:index,chapterId:chapter.id,block:match.block,textOffset,offset:0,textY:position.textY??140,version:this.book.version,
      ...(context?{anchor:{quote:context.quote,prefix:context.prefix,suffix:context.suffix,offset:textOffset-quoteStart},chapterFraction:context.fraction}:{})};
  }
  stopFlowPreparation(){
    this.chapterObserver?.disconnect();this.chapterObserver=null;
  }
  scheduleFlowPreparation(){
    this.stopFlowPreparation();if(this.settings.mode==='tap')return;
    const generation=this.generation;
    this.chapterObserver=new IntersectionObserver(entries=>{
      if(generation!==this.generation)return;
      for(const entry of entries)if(entry.isIntersecting)this.prepareFlowChapter(Number(entry.target.dataset.index)).catch(console.error);
    },{rootMargin:'200px 0px'});
    for(const section of this.flow.children)if(section.classList.contains('chapter-pending'))this.chapterObserver.observe(section);
  }
  videoPreview(frame){
    // Keep YouTube detached until a deliberate tap. The preview reserves the same
    // space as the player. Its cropped thumbnail is bundled locally, so the
    // preview doesn't contact YouTube or need the player API.
    const preview=document.createElement('button');preview.type='button';preview.className='book-video-preview';
    frame.parentElement.classList.add('book-video-shell-audiobook');
    frame.title=t("ui-extremely-hydrated-man-reads-easypeasymethodorg-audiobook");
    preview.setAttribute('aria-label',t("ui-play-easypeasy-video-on-youtube"));
    preview.innerHTML='<span class="video-preview-title"></span><span class="video-preview-play"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 11 7-11 7z" fill="currentColor"/></svg></span><span class="video-preview-footer"><span class="video-preview-caption">Hackauthor²</span><span class="video-preview-source"><svg viewBox="0 0 28 20" aria-hidden="true"><rect x="0" y="0" width="28" height="20" rx="6" fill="currentColor"/><path d="m11 5 8 5-8 5z" fill="#151515"/></svg>YouTube</span></span>';
    preview.querySelector('.video-preview-title').textContent=frame.title;
    const thumbnail=document.createElement('img');thumbnail.className='video-preview-thumbnail';thumbnail.src=new URL('images/easypeasy-video-preview-left.jpg',document.baseURI).href;thumbnail.alt='';thumbnail.loading='lazy';thumbnail.width=360;thumbnail.height=270;preview.prepend(thumbnail);
    preview.addEventListener('click',()=>{
      if(this.pickingAudio)return;
      const url=new URL(frame.src);url.searchParams.set('autoplay','1');url.searchParams.set('playsinline','1');
      frame.src=url.href;frame.loading='eager';
      preview.replaceWith(frame);frame.focus({preventScroll:true});
    });
    frame.replaceWith(preview);
  }
  observeBlocks(){this.visible.clear();this.observer?.disconnect();this.observer=new IntersectionObserver(entries=>{for(const entry of entries){if(entry.isIntersecting){this.visible.add(entry.target);if(entry.target.classList.contains('intentional-blank'))setTimeout(()=>{if(this.visible.has(entry.target))entry.target.querySelector('.blank-explanation')?.classList.add('visible');},4500);}else this.visible.delete(entry.target);}this.scheduleProgress();},{rootMargin:'-100px 0px -10% 0px',threshold:0});this.flow.querySelectorAll('.book-chapter,[id],.intentional-blank').forEach(el=>this.observer.observe(el));}
  textRange(block,offset=0){
    const walker=document.createTreeWalker(block,NodeFilter.SHOW_TEXT);let node,last;
    while((node=walker.nextNode())){last=node;if(offset<node.length){const range=document.createRange();range.setStart(node,Math.max(0,offset));range.setEnd(node,Math.min(node.length,Math.max(0,offset)+1));return range;}offset-=node.length;}
    if(last){const range=document.createRange();range.setStart(last,last.length);range.collapse(true);return range;}return null;
  }
  visibleTextOffset(block,left,right,top){
    const walker=document.createTreeWalker(block,NodeFilter.SHOW_TEXT);let node,total=0;
    while((node=walker.nextNode())){
      const range=document.createRange();range.selectNodeContents(node);
      if([...range.getClientRects()].some(rect=>rect.right>left&&rect.left<right&&rect.bottom>top)){
        let low=0,high=node.length;
        while(low<high){const mid=(low+high)>>1;range.setStart(node,mid);range.setEnd(node,Math.min(node.length,mid+1));const rect=range.getBoundingClientRect();const laterColumn=this.flow.dir==='rtl'?rect.right<=left:rect.left>=right;if(laterColumn||(rect.right>left&&rect.left<right&&rect.bottom>top))high=mid;else low=mid+1;}
        return total+low;
      }
      total+=node.length;
    }
    return 0;
  }
  tapSize(){const style=getComputedStyle(this.stage);return {width:this.stage.clientWidth,height:Math.max(1,Math.round(this.stage.clientHeight-parseFloat(style.paddingTop)-parseFloat(style.paddingBottom)))};}
  scheduleResize(){
    if(this.settings.mode==='tap'&&this.flow&&this.tapWidth){const size=this.tapSize();if(this.tapWidth!==size.width||this.tapHeight!==size.height)this.pendingResizePosition ||= this.resizeAnchor||this.position;}
    clearTimeout(this.resizeTimer);this.resizeTimer=setTimeout(()=>this.resize(),150);
  }
  layoutFlow(position){
    this.stopPageTurn();this.clearBoundaryCue();
    this.pendingResizePosition=null;this.resizeAnchor=null;
    // Keep the document's scroll range while pages occupy the visible viewport.
    // Collapsing it or scrolling to the reader's top can reveal mobile browser bars.
    if(this.settings.mode==='tap'&&!this.stage.classList.contains('tap-shell'))this.style.height=this.getBoundingClientRect().height+'px';
    this.flow.style.transform='';
    this.flow.style.height='';this.flow.style.width='';
    this.flow.className='book-flow';
    this.stage.classList.toggle('tap-shell',this.settings.mode==='tap');
    this.stage.dir=this.flow.dir;
    this.stage.querySelectorAll('.tap-edge').forEach(edge=>edge.remove());
    const target=this.settings.mode==='tap'?this.chapterIndex(position):null;
    for(const section of this.flow.children)section.classList.toggle('tap-current',Number(section.dataset.index)===target);
    if(this.settings.mode==='tap'){
      this.tapChapter=target;
      // Freeze the columns while browser bars animate; repaginate once they settle.
      const size=this.tapSize();this.flow.style.height=size.height+'px';this.flow.style.width=size.width+'px';this.flow.style.setProperty('--tap-page-height',size.height+'px');
      for(const [action,caption]of [['previous',t("ui-previous-page")],['next',t("ui-next-page")],['menu',t("ui-open-reader-menu")],['appearance',t("ui-open-appearance-menu")]]){
        const edge=document.createElement('button');edge.type='button';edge.className='tap-edge '+action;edge.setAttribute('aria-label',caption);edge.addEventListener('click',()=>this.emit('readingGesture',{action}));this.stage.append(edge);
      }
      const style=getComputedStyle(this.flow),padding=parseFloat(style.paddingLeft)+parseFloat(style.paddingRight);
      this.pageWidth=this.flow.clientWidth-padding;
      this.pageStep=this.pageWidth+48;
      this.pageCount=Math.max(1,Math.ceil((this.flow.scrollWidth-padding+48)/this.pageStep));
      this.tapWidth=this.flow.clientWidth;this.tapHeight=this.flow.clientHeight;
      const block=!Number.isFinite(position?.openingScrollY)&&position?.block&&this.flowAnchor(position);
      let rect;
      if(block&&Number.isFinite(position.textOffset))rect=this.textRange(block,position.textOffset)?.getBoundingClientRect();
      if(!rect&&block){let remaining=(position?.offset||0)*[...block.getClientRects()].reduce((sum,r)=>sum+r.height,0);rect=[...block.getClientRects()].find(r=>{if(remaining<r.height)return true;remaining-=r.height;return false;})||block.getBoundingClientRect();}
      const bounds=this.flow.getBoundingClientRect(),left=bounds.left+parseFloat(style.paddingLeft),right=bounds.right-parseFloat(style.paddingRight);
      this.pageIndex=position?.end?this.pageCount-1:rect?Math.floor(((this.flow.dir==='rtl'?right-rect.right:rect.left-left)+1)/this.pageStep):0;
      this.drawPage();
    }else{this.style.height='';if(position)this.restoreFlow(position);}
    this.updatePages();
  }
  updatePages(){
    if(!this.flow||this.settings.mode==='tap')return;
    this.pageStep=Math.max(120,(window.visualViewport?.height||innerHeight)-100);
    const rect=this.flow.getBoundingClientRect(),top=scrollY+rect.top;
    this.pageCount=Math.max(1,Math.ceil(rect.height/this.pageStep));
    this.pageIndex=Math.max(0,Math.min(this.pageCount-1,Math.floor((scrollY-top)/this.pageStep+.5)));
  }
  drawPage(animate=false){
    this.clearBoundaryCue();
    this.pageIndex=Math.max(0,Math.min(this.pageCount-1,this.pageIndex));
    for(const [action,unavailable]of [['previous',this.tapChapter===0&&this.pageIndex===0],['next',this.tapChapter===this.book.chapters.length-1&&this.pageIndex===this.pageCount-1]])this.stage.querySelector('.tap-edge.'+action)?.setAttribute('aria-disabled',String(unavailable));
    this.flow.style.transform=`translateX(${(this.flow.dir==='rtl'?1:-1)*this.pageIndex*this.pageStep}px)`;
    this.pageAnimation?.cancel();
    if(animate&&!this.settings.reduced)this.pageAnimation=this.flow.animate([{opacity:.6},{opacity:1}],{duration:120,easing:'ease-out'});
    this.position=this.capture();this.scheduleProgress();
  }
  restoreFlow(position){
    this.ensureFlowPosition(position);
    // The title and cover sit above the first reading anchor. Keep that opening viewport.
    if(Number.isFinite(position.openingScrollY)){window.scrollTo({top:Math.max(0,position.openingScrollY||0),behavior:'instant'});return;}
    const el=this.flowAnchor(position);if(!el)return;
    // Skipped chapters use estimated heights until laid out. Resolve them before locating the anchor.
    const chapter=el.closest('.book-chapter');
    this.revealFlowThrough(chapter);
    if((position.fromTap||Number.isFinite(position.textY))&&Number.isFinite(position.textOffset)){const rect=this.textRange(el,position.textOffset)?.getBoundingClientRect();if(rect){window.scrollTo({top:scrollY+rect.top-(position.textY??24),behavior:'instant'});return;}}
    const rect=el.getBoundingClientRect();window.scrollTo({top:window.scrollY+rect.top-140+(position.offset || 0)*rect.height,behavior:'instant'});}
  revealFlowThrough(chapter){chapter.style.contentVisibility='visible';}
  capture(preserveText=false){
    if(!this.book)return null;
    if(this.pdf)return this.pdf.position;
    if(!this.flow)return null;
    if(this.modeAnimation?.playState==='running')return this.position;
    if(this.settings.mode==='tap'&&(this.pendingResizePosition||this.resizeAnchor))return this.pendingResizePosition||this.resizeAnchor;
    const tap=this.settings.mode==='tap';
    let chapter,block,textOffset=0,textY;
    if(tap){
      const style=getComputedStyle(this.flow),left=this.flow.getBoundingClientRect().left+(this.flow.dir==='rtl'?-1:1)*this.pageIndex*this.pageStep+parseFloat(style.paddingLeft),right=left+this.pageWidth;
      block=[...this.flow.querySelectorAll('[id]:not(.book-chapter)')].find(el=>[...el.getClientRects()].some(rect=>rect.right>left&&rect.left<right));
      chapter=block?.closest('.book-chapter')||[...this.flow.children].find(el=>[...el.getClientRects()].some(rect=>rect.right>left&&rect.left<right))||this.flow.firstElementChild;
      block ||= chapter;
      if(block){textOffset=this.visibleTextOffset(block,left,right,this.stage.getBoundingClientRect().top);textY=this.textRange(block,textOffset)?.getBoundingClientRect().top;}
    }else{
      // Observer entries can lag behind explicit navigation and chapter layout.
      chapter=[...this.flow.children].find(el=>el.getBoundingClientRect().bottom>140)||this.flow.lastElementChild;
      if(!chapter||chapter.classList.contains('chapter-pending'))return this.position||null;
      block=[...chapter.querySelectorAll('[id]')].find(el=>el.getBoundingClientRect().bottom>140)||chapter;
      textOffset=this.visibleTextOffset(block,0,innerWidth,140);
      if(preserveText)textY=this.textRange(block,textOffset)?.getBoundingClientRect().top;
    }
    if(!chapter)return null;
    if(chapter.classList.contains('chapter-pending'))return this.position||null;
    const index=Number(chapter.dataset.index),rect=chapter.getBoundingClientRect();
    const local=Math.max(0,Math.min(1,(140-rect.top)/rect.height));
    const before=this.book.chapters.slice(0,index).reduce((sum,c)=>sum+c.wordCount,0);
    const words=this.book.wordCount||this.book.chapters.reduce((sum,c)=>sum+c.wordCount,0)||1;
    const fraction=tap?this.pageIndex/this.pageCount:local;
    const percent=(before+fraction*(this.book.chapters[index]?.wordCount||0))/words*100;
    const brect=block?.getBoundingClientRect();
    const openingScrollY=!tap&&(window.scrollY<=0||this.flow.firstElementChild.getBoundingClientRect().top>140)?Math.max(0,window.scrollY):null;
    const quoteStart=Math.max(0,textOffset-32),context=textContext(block,quoteStart,Math.min(block?.textContent.length||0,quoteStart+96));
    const anchor=context?{quote:context.quote,prefix:context.prefix,suffix:context.suffix,offset:textOffset-quoteStart}:null;
    return {kind:'flow',openingScrollY,chapter:index,chapterId:chapter.id,block:block?.id||chapter.id,offset:tap?0:Math.max(0,Math.min(1,(140-(brect?.top??140))/(brect?.height||1))),percent,textOffset,version:this.book.version,anchor,chapterFraction:context?.fraction??fraction,...(tap?{fromTap:true,textY}:preserveText?{textY}:{})};
  }
  // Keep updates coming during continuous scrolling, even with the controls hidden.
  scheduleProgress(){if(this.progressTimer)return;this.progressTimer=setTimeout(()=>{this.progressTimer=0;this.reportProgress();},120);}
  flowChapterProgress(chapter){
    const element=this.flow.children[chapter];if(!element)return {percent:0,page:0,total:0};
    if(this.settings.mode==='tap'){
      const style=getComputedStyle(this.flow),bounds=this.flow.getBoundingClientRect(),rtl=this.flow.dir==='rtl';
      const edge=rtl?bounds.right-parseFloat(style.paddingRight):bounds.left+parseFloat(style.paddingLeft);
      const pages=[...element.getClientRects()].map(rect=>Math.floor(((rtl?edge-rect.right:rect.left-edge)+1)/this.pageStep));
      const first=Math.min(...pages),last=Math.max(...pages),total=last-first+1,page=Math.max(1,Math.min(total,this.pageIndex-first+1));
      return {percent:total>1?(page-1)/(total-1)*100:0,page,total};
    }
    const rect=element.getBoundingClientRect(),viewport=window.visualViewport?.height||innerHeight;
    // Finish on the chapter's last screen; short chapters still advance as they scroll past.
    const distance=rect.height>viewport-140?rect.height-(viewport-140):rect.height;
    return {percent:Math.max(0,Math.min(100,(140-rect.top)/Math.max(1,distance)*100)),page:0,total:0};
  }
  reportProgress(){if(this.pdf)return;if(this.restoring || this.pendingResizePosition || !this.book)return;const position=this.capture();if(!position)return;this.position=position;const chapter=position.chapter || 0;let label=this.book.chapters?.[chapter]?.title || this.book.title;let page=0,total=0,remaining=0;
    this.sampleReadingPace(position);let percent=position.percent||0;
    {this.updatePages();({percent,page,total}=this.flowChapterProgress(chapter));const c=this.book.chapters[chapter];remaining=Math.max(0,Math.ceil((c?.wordCount||0)*(1-percent/100)/this.readingPace()));if(this.settings.mode==='tap')label+=` · ${page}/${total}`;}
    // Presentation is chapter-relative; saved positions retain their whole-book percentage.
    this.emit('progress',{percent:Math.min(100,Math.max(0,percent)),chapter,label,remaining,page,total});this.emit('position',position);
  }
  readingPace(){return this.currentPace;}
  resetReadingPace(){this.emit('paceReset');this.nextPaceSample=0;}
  readingWordOffset(position){
    const chapter=this.flow?.children[position.chapter],body=chapter?.querySelector('.chapter-body'),block=this.flowAnchor(position);
    if(!body||!block||!body.contains(block))return 0;
    const anchor=this.textRange(block,position.textOffset||0);if(!anchor)return 0;
    const prefix=document.createRange();prefix.selectNodeContents(body);prefix.setEnd(anchor.startContainer,anchor.startOffset);
    return prefix.toString().match(/\S+/g)?.length||0;
  }
  sampleReadingPace(position){
    if(position.kind!=='flow'||document.hidden||!document.hasFocus()||this.audioPlaying||this.pickingAudio||document.querySelector('.panel-overlay,:popover-open')){this.resetReadingPace();return;}
    const now=performance.now();if(now<this.nextPaceSample)return;this.nextPaceSample=now+1000;
    const media=[...this.shadowRoot.querySelectorAll('video,audio')].some(el=>!el.paused&&!el.ended);
    const video=[...this.shadowRoot.querySelectorAll('iframe')].some(el=>{const rect=el.getBoundingClientRect();return rect.bottom>0&&rect.top<innerHeight;});
    if(media||video){this.resetReadingPace();return;}
    this.emit('paceSample',{book:this.book.id,chapter:position.chapter,language:this.book.language,words:this.readingWordOffset(position),time:now});
  }
  stopPageTurn(){this.pageAnimation?.cancel();this.modeAnimation?.cancel();this.modeAnimation=null;}
  clearBoundaryCue(){clearTimeout(this.boundaryTimer);this.boundaryAnimation?.cancel();this.boundaryAnimation=null;this.boundaryElement?.remove();this.boundaryElement=null;}
  showBoundaryCue(direction){
    this.clearBoundaryCue();
    const cue=document.createElement('div');cue.className='tap-boundary '+(direction<0?'previous':'next');cue.setAttribute('aria-hidden','true');this.stage.append(cue);this.boundaryElement=cue;
    const reduced=this.settings.reduced||matchMedia('(prefers-reduced-motion:reduce)').matches;
    if(!reduced)this.boundaryAnimation=cue.animate([{opacity:.55},{opacity:0}],{duration:260,easing:'ease-out'});
    this.boundaryTimer=setTimeout(()=>this.clearBoundaryCue(),reduced?160:260);
  }
  animateModeChange(position,before){
    if(this.settings.reduced||matchMedia('(prefers-reduced-motion:reduce)').matches||!before||before.bottom<=0||before.top>=innerHeight)return;
    const block=this.flowAnchor(position),after=block&&this.textRange(block,position.textOffset||0)?.getBoundingClientRect();
    if(!after||after.bottom<=0||after.top>=innerHeight)return;
    const distance=before.top-after.top;if(Math.abs(distance)<1)return;
    // Only the mode change follows the reading line vertically. Page turns keep
    // their existing fade, with no directional animation.
    this.position=this.capture();
    const transform=this.flow.style.transform||'translateX(0px)';
    const animation=this.flow.animate([{transform:transform+` translateY(${distance}px)`},{transform}],{duration:180,easing:'ease-out'});
    this.modeAnimation=animation;
    animation.finished.then(()=>{if(this.modeAnimation===animation){this.modeAnimation=null;this.scheduleProgress();}},()=>{});
  }
  async turnFlowPage(direction){
    if(this.pageTurning)return;
    if(this.pendingResizePosition)this.layoutFlow(this.pendingResizePosition);
    const next=this.pageIndex+direction;
    if(next>=0&&next<this.pageCount){this.resizeAnchor=null;this.tapOrigin=null;this.pageIndex=next;this.drawPage(true);return;}
    const chapter=this.tapChapter+direction;if(chapter<0||chapter>=this.book.chapters.length){this.showBoundaryCue(direction);return;}
    this.resizeAnchor=null;this.tapOrigin=null;
    this.pageTurning=true;const generation=this.generation;
    try{await this.go({kind:'flow',chapter,chapterId:this.book.chapters[chapter].id,end:direction<0});}finally{if(generation===this.generation)this.pageTurning=false;}
  }
  async turn(direction){if(this.pdf)return;if(this.settings.mode==='tap')return this.turnFlowPage(direction);const c=this.capture()?.chapter||0;return this.chapter(c+direction);}
  async chapter(index,announce=true){
    if(this.pdf)return;index=Math.max(0,Math.min(this.book.chapters.length-1,index));
    const chapter=this.book.chapters[index];if(!chapter)return;
    await this.go({kind:'flow',chapter:index,chapterId:chapter.id,block:chapter.id+'-audio-title'});
    if(announce)this.emit('toast',chapter.title);
  }
  async go(location){
    this.resetReadingPace();this.stopPageTurn();let position;try{position=typeof location==='string'?JSON.parse(location):location;}catch{return;}
    if(!position)return;
    if(this.pdf){this.pdf.position={...this.pdf.position,...position,kind:'pdf'};this.stage.querySelector('iframe').src=this.pdf.url+'#page='+Math.max(1,Number(position.page)||1);return;}
    const epoch=this.navigationEpoch=(this.navigationEpoch||0)+1,generation=this.generation;
    await this.prepareFlowChapter(this.chapterIndex(position));
    if(generation!==this.generation||epoch!==this.navigationEpoch)return;
    position=await this.resolveFlowPosition(position);
    if(generation!==this.generation||epoch!==this.navigationEpoch)return;
    this.tapOrigin=null;if(this.settings.mode==='tap')this.layoutFlow(position);else this.restoreFlow(position);
    this.updatePages();this.scheduleProgress();
  }
  async apply(settings,readingPosition=null){
    this.resetReadingPace();
    const changed=this.settings.mode!==settings.mode;
    if(changed)this.navigationEpoch++;
    const origin=changed&&this.settings.mode==='tap'?this.tapOrigin:null;
    let position=origin||readingPosition||(this.settings.mode==='tap'?(this.position||this.capture()):this.capture(true));
    const block=changed&&position&&this.flowAnchor(position),before=block&&this.textRange(block,position.textOffset||0)?.getBoundingClientRect();
    const handoff=changed&&settings.mode==='scroll'&&this.scrollHandoff&&performance.now()-this.scrollHandoff.time<1000?this.scrollHandoff:null;
    this.stopPageTurn();this.scrollHandoff=null;
    if(handoff&&before){
      const travelled=handoff.gesture?-handoff.gesture.dy:handoff.delta,native=window.scrollY-handoff.scrollY;
      const distance=native*travelled>0&&Math.abs(native)>Math.abs(travelled)?native:travelled;
      position={...position,openingScrollY:null,fromTap:true,textY:before.top-distance};
    }
    this.settings=settings;
    if(this.pdf)return;
    this.layoutFlow(position);
    this.tapOrigin=changed&&settings.mode==='tap'?position:null;
    if(changed&&!handoff)this.animateModeChange(position,before);
    this.scheduleFlowPreparation();
  }
  resize(force=false){
    if(this.restoring||!this.book)return;
    this.resetReadingPace();
    if(this.pdf)return;
    if(this.settings.mode==='tap'){
      const size=this.tapSize();
      if(!force&&this.tapWidth===size.width&&this.tapHeight===size.height)return;
      const position=this.pendingResizePosition||this.resizeAnchor||this.position||this.capture();
      this.layoutFlow(position);
      // Repeated toolbar changes must keep the same character, rather than
      // gradually walking backwards to each newly exposed page opening.
      const block=position?.block&&this.flowAnchor(position);
      this.resizeAnchor={...position,percent:this.capture()?.percent??position?.percent,textY:block?this.textRange(block,position.textOffset||0)?.getBoundingClientRect().top:position?.textY};
      this.position=this.resizeAnchor;
    }else{this.updatePages();this.scheduleProgress();}
  }
  inlineAudioTarget(){
    if(this.book?.id!=='easypeasy-en'||!this.flow)return null;
    const block=[...this.flow.children[0].querySelectorAll('.chapter-body p')].find(el=>el.textContent.trim().startsWith('Perhaps you identify with the following questions'));
    return block?this.listeningTarget(block):null;
  }
  listeningTarget(block=null,offset=0){
    const blocks=this.speakingBlocks();
    if(!block){
      block=blocks.find(el=>{const r=el.getBoundingClientRect();return r.bottom>140&&r.top<innerHeight-80&&r.right>0&&r.left<innerWidth;})||blocks[0];
    }
    if(!block)return null;
    const chapter=block.closest('.book-chapter'),chapterIndex=chapter?Number(chapter.dataset.index):this.capture()?.chapter||0;
    const chapterBlocks=blocks.filter(el=>chapter?el.closest('.book-chapter')===chapter:true);
    const words=text=>(text.match(/\S+/gu)||[]).length;
    const index=chapterBlocks.indexOf(block),before=chapterBlocks.slice(0,Math.max(0,index)).reduce((sum,el)=>sum+words(el.textContent),0);
    return {block,blockId:block.id,offset,chapter:chapterIndex,chapterId:this.book.chapters?.[chapterIndex]?.id,fraction:(before+words(block.textContent.slice(0,offset)))/Math.max(1,chapterBlocks.reduce((sum,el)=>sum+words(el.textContent),0))};
  }
  pickAudioWord(event){
    const block=event.composedPath().find(el=>el?.matches?.('.chapter-body p,.chapter-body li,.chapter-title,.chapter-body h2,.chapter-body h3'));
    if(!block)return;
    const word=wordAtPoint(block,event.clientX,event.clientY);
    if(word)this.emit('audioWord',this.listeningTarget(block,word.offset));
  }
  linkClick(event){if(this.pickingAudio){event.preventDefault();this.pickAudioWord(event);return;}const anchor=event.composedPath().find(el=>el?.tagName==='A');if(!anchor)return;const href=anchor.getAttribute('href');if(href?.startsWith('#')){event.preventDefault();this.go({kind:'flow',block:decodeURIComponent(href.slice(1))});}else if(this.book?.kind==='pollen'&&href){try{const url=new URL(href);const id=decodeURIComponent(url.pathname.split('/').pop().replace(/\.html$/,''));const chapter=this.book.chapters.findIndex(c=>c.id===id);if(chapter>=0){event.preventDefault();this.chapter(chapter);}}catch{}}}
  selection(){if(this.annotations)return;const sel=this.shadowRoot.getSelection?.()||window.getSelection();const value=sel?.toString()?.trim();this.emit('selection',value?.slice(0,1200)||'');}
  currentText(){if(this.pdf)return '';const p=this.capture();const el=this.flowAnchor(p);return el?.textContent||this.flow?.textContent||'';}
  speakingBlocks(){this.ensureFlowThrough();if(this.pdf)return [];return [...this.flow.querySelectorAll('.chapter-body p,.chapter-body li,.chapter-title,.chapter-body h2,.chapter-body h3')].filter(el=>el.textContent.trim());}
  async search(query){
    // Extract inert DOM text without preparing offscreen reading chapters.
    // Keep native locale-sensitive folding; Elm owns matching and result models.
    if(!this.pdf&&this.book&&!this.searchBlocks){
      this.searchBlocks=[];
      for(const chapter of this.book.chapters){
        const holder=document.createElement('template');holder.innerHTML=chapter.html;
        for(const element of holder.content.querySelectorAll('p,li,h2,h3,blockquote')){
          const text=element.textContent,folded=text.toLocaleLowerCase(),boundaries=[];
          // Expanded lowercase characters (such as İ) must keep their original
          // UTF-16 boundaries. Most blocks need no extra mapping.
          if(folded.length!==text.length){
            let start=0;
            for(const character of text){
              const end=start+character.length;
              for(let i=0;i<character.toLocaleLowerCase().length;i++)boundaries.push({start,end});
              start=end;
            }
          }
          this.searchBlocks.push({chapterId:chapter.id,id:element.id,title:chapter.title,text,folded,boundaries});
        }
      }
    }
    query=(query||'').trim();
    return policy('search',{query,foldedQuery:query.toLocaleLowerCase(),pdf:!!this.pdf,blocks:this.searchBlocks||[]});
  }

  async openPdf(file,position){
    this.file=file;const url=URL.createObjectURL(file);this.pdf={url,position:position||{kind:'pdf',page:1,offset:0,percent:0,chapter:0}};
    const opening=openingControls();opening.classList.add('pdf-opening');
    this.stage.replaceChildren(opening,pdfView(url+'#page='+Math.max(1,Number(position?.page)||1),this.book.title));this.emit('toc',[]);
  }
  async print(){if(this.pdf){window.open(this.pdf.url,'_blank','noopener');return;}return openPdfPreview(this.book,{layout:'print'});}
}
customElements.define('reader-engine',ReaderEngine);
